import React, { useEffect, useState } from 'react';
import { arrayUnion, deleteField, doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { AlertCircle, ArrowLeft, Check, ChevronDown, CircleCheck, MoreHorizontal, Printer, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from './Spinner.js';
import toast from 'react-hot-toast';
import AttachmentCard from './components/AttachmentCard.js';
import './PaymentRequestPreview.css';
import { buildPaymentRequestWorkflow, getStatusAfterAccountsCheck, getStatusAfterHrReview } from './paymentRequestWorkflow.js';

const formatAmount = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
})}`;

const formatDate = (date) => {
  if (!date) return '—';
  const parsedDate = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return date;
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(parsedDate);
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const getToday = () => new Date().toISOString().slice(0, 10);

const PAYMENT_PROCESSORS = ['Tikam Chand', 'Saurabh Ganeriwal', 'Jasveer Singh'];

const emptyPaymentForm = () => ({
  settledAmount: '',
  tdsAmount: '',
  otherDeductions: '',
  paymentDate: getToday(),
  paymentMode: '',
  paymentReference: '',
  processedByName: '',
  remarks: ''
});

const PaymentRequestPreview = ({ db, user }) => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [managerReviewRemarks, setManagerReviewRemarks] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);
  const [accountsCheckRemarks, setAccountsCheckRemarks] = useState('');
  const [isAccountsChecking, setIsAccountsChecking] = useState(false);
  const [hrReviewRemarks, setHrReviewRemarks] = useState('');
  const [isHrReviewing, setIsHrReviewing] = useState(false);
  const [adminReviewRemarks, setAdminReviewRemarks] = useState('');
  const [isAdminReviewing, setIsAdminReviewing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  useEffect(() => {
    const loadRequest = async () => {
      try {
        const snapshot = await getDoc(doc(db, 'payment_requests', requestId));
        if (!snapshot.exists()) {
          setError('Payment request not found.');
          return;
        }
        setRequest({ id: snapshot.id, ...snapshot.data() });
      } catch (loadError) {
        console.error('Unable to load payment request:', loadError);
        setError('Unable to load this payment request.');
      } finally {
        setLoading(false);
      }
    };

    loadRequest();
  }, [db, requestId]);

  if (loading) return <Spinner />;

  const lineItems = request?.lineItems || [];
  const displayLineItems = [
    ...lineItems,
    ...Array.from({ length: Math.max(0, 2 - lineItems.length) }, () => ({}))
  ];
  const approvalWorkflow = [
    { stage: 'VERIFIED BY HOD', review: request?.managerReview },
    { stage: 'ACCOUNTS CHECKED', review: request?.accountsCheckReview },
    { stage: 'FINANCE APPROVAL', review: request?.hrReview },
    { stage: 'FINAL AUTHORISED', review: request?.adminReview }
  ];
  const normalizedUserEmail = String(user?.email || '').trim().toLowerCase();
  const canManagerReview = request?.status === 'Waiting Manager Approval'
    && String(request?.managerEmail || '').trim().toLowerCase() === normalizedUserEmail;
  const canManageOwnRequest = request?.requesterUid === user?.uid;
  const showManagerReviewOutcome = canManageOwnRequest
    && request?.managerReview
    && ['Waiting Accounts Check', 'ReturnedByManager'].includes(request.status);
  const canAccountsCheck = request?.status === 'Waiting Accounts Check'
    && user?.paymentRequestApprovalRoles?.includes('accounts_check');
  const canHrReview = request?.status === 'Waiting HR Review'
    && user?.paymentRequestApprovalRoles?.includes('hr_review');
  const canAdminReview = request?.status === 'Waiting Admin Approval'
    && user?.paymentRequestApprovalRoles?.includes('admin_approval');
  const canProcessPayment = ['Waiting Accounts Payment', 'Partially Paid'].includes(request?.status)
    && user?.paymentRequestApprovalRoles?.includes('accounts_payment');
  const payments = request?.payments || [];
  const totalSettledAmount = payments.reduce((total, payment) => total + Number(payment?.settledAmount || 0), 0);
  const totalTdsAmount = payments.reduce((total, payment) => total + Number(payment?.tdsAmount || 0), 0);
  const totalOtherDeductions = payments.reduce((total, payment) => total + Number(payment?.otherDeductions || 0), 0);
  const totalNetPaid = payments.reduce((total, payment) => total + Number(payment?.netPayment || 0), 0);
  const remainingBalance = Math.max(0, Number(request?.requestedAmount || 0) - totalSettledAmount);
  const latestPayment = payments.length > 0 ? payments[payments.length - 1] : null;
  const canViewPaymentHistory = payments.length > 0
    && (canManageOwnRequest || String(user?.role || '').toLowerCase() === 'accounts');
  const canCreateOrContinueSettlement = canManageOwnRequest
    && request?.requestType === 'Employee Advance'
    && ['Advance Paid Awaiting Settlement', 'Settlement Under Review', 'Settlement Returned for Revision'].includes(request?.status);
  const showAccountsCheckOutcome = canManageOwnRequest
    && request?.accountsCheckReview
    && ['Waiting HR Review', 'Waiting Admin Approval', 'Waiting Accounts Payment', 'ReturnedByAccounts'].includes(request.status);
  const showHrReviewOutcome = canManageOwnRequest
    && request?.hrReview
    && ['Waiting Admin Approval', 'Waiting Accounts Payment', 'ReturnedByHR'].includes(request.status);
  const showAdminReviewOutcome = canManageOwnRequest
    && request?.adminReview
    && ['Waiting Accounts Payment', 'ReturnedByAdmin'].includes(request.status);

  const submitRequest = async () => {
    setIsSubmitting(true);
    try {
      const workflow = buildPaymentRequestWorkflow(user, request?.requestedAmount);
      let managerEmail = '';
      let managerName = '';

      if (!workflow.skipManagerApproval) {
        const requesterEmail = String(request?.requesterEmail || user?.email || '').trim().toLowerCase();
        if (!requesterEmail) throw new Error('Unable to identify the requester.');

        const requesterSnapshot = await getDoc(doc(db, 'authorized_users', requesterEmail));
        managerEmail = String(requesterSnapshot.data()?.reportingManagerEmail || '').trim().toLowerCase();
        if (!managerEmail) throw new Error('No reporting manager has been assigned to this user.');

        const managerSnapshot = await getDoc(doc(db, 'authorized_users', managerEmail));
        managerName = managerSnapshot.data()?.displayName || managerEmail;
      }

      const status = workflow.initialStatus;
      const submissionAction = workflow.submissionAction;
      await updateDoc(doc(db, 'payment_requests', requestId), {
        status,
        workflow,
        ...(workflow.skipManagerApproval
          ? { managerEmail: deleteField(), managerName: deleteField() }
          : { managerEmail, managerName }),
        updatedAt: serverTimestamp(),
        submittedAt: serverTimestamp(),
        auditTrail: arrayUnion({
          action: submissionAction,
          at: new Date().toISOString(),
          byUid: user?.uid || '',
          byName: user?.displayName || user?.email || ''
        })
      });
      setRequest((current) => ({
        ...current,
        status,
        workflow,
        managerEmail,
        managerName
      }));
      toast.success('Payment request submitted.');
    } catch (submitError) {
      console.error('Unable to submit payment request:', submitError);
      toast.error(submitError?.message === 'No reporting manager has been assigned to this user.'
        ? submitError.message
        : 'Unable to submit the payment request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManagerDecision = async (decision) => {
    const remarks = managerReviewRemarks.trim();
    if (decision === 'rejected' && !remarks) {
      toast.error('Review remarks are required when rejecting a request.');
      return;
    }
    if (!window.confirm(`Are you sure you want to ${decision === 'approved' ? 'approve' : 'reject'} this payment request?`)) return;

    setIsReviewing(true);
    try {
      const reviewedAt = new Date().toISOString();
      const nextStatus = decision === 'approved' ? 'Waiting Accounts Check' : 'ReturnedByManager';
      const managerReview = {
        decision,
        remarks,
        reviewedBy: user?.displayName || user?.email || '',
        reviewedByEmail: user?.email || '',
        reviewedAt
      };

      await updateDoc(doc(db, 'payment_requests', requestId), {
        status: nextStatus,
        managerReview,
        updatedAt: serverTimestamp(),
        auditTrail: arrayUnion({
          action: decision === 'approved' ? 'approved_by_manager' : 'rejected_by_manager',
          at: reviewedAt,
          byUid: user?.uid || '',
          byName: user?.displayName || user?.email || '',
          remarks
        })
      });
      setRequest((current) => ({ ...current, status: nextStatus, managerReview }));
      setManagerReviewRemarks('');
      toast.success(decision === 'approved' ? 'Payment request approved.' : 'Payment request rejected.');
    } catch (reviewError) {
      console.error('Unable to review payment request:', reviewError);
      toast.error('Unable to save the manager review. Please try again.');
    } finally {
      setIsReviewing(false);
    }
  };

  const handleAccountsCheckDecision = async (decision) => {
    const remarks = accountsCheckRemarks.trim();
    if (decision === 'rejected' && !remarks) {
      toast.error('Review remarks are required when returning a request.');
      return;
    }
    if (!window.confirm(`Are you sure you want to ${decision === 'approved' ? 'approve' : 'return'} this payment request?`)) return;

    setIsAccountsChecking(true);
    try {
      const reviewedAt = new Date().toISOString();
      const nextStatus = decision === 'approved'
        ? getStatusAfterAccountsCheck(request?.workflow, request?.requestedAmount)
        : 'ReturnedByAccounts';
      const accountsCheckReview = {
        decision,
        remarks,
        reviewedBy: user?.displayName || user?.email || '',
        reviewedByEmail: user?.email || '',
        reviewedAt
      };

      await updateDoc(doc(db, 'payment_requests', requestId), {
        status: nextStatus,
        accountsCheckReview,
        updatedAt: serverTimestamp(),
        auditTrail: arrayUnion({
          action: decision === 'approved' ? 'checked_by_accounts' : 'returned_by_accounts',
          at: reviewedAt,
          byUid: user?.uid || '',
          byName: user?.displayName || user?.email || '',
          remarks
        })
      });
      setRequest((current) => ({ ...current, status: nextStatus, accountsCheckReview }));
      setAccountsCheckRemarks('');
      toast.success(decision === 'approved'
        ? (nextStatus === 'Waiting HR Review' ? 'Payment request sent to HR for review.' : nextStatus === 'Waiting Admin Approval' ? 'Payment request sent to Admin for approval.' : 'Payment request sent to Accounts for payment processing.')
        : 'Payment request returned by Accounts.');
    } catch (reviewError) {
      console.error('Unable to complete Accounts check:', reviewError);
      toast.error('Unable to save the Accounts check. Please try again.');
    } finally {
      setIsAccountsChecking(false);
    }
  };

  const handleHrDecision = async (decision) => {
    const remarks = hrReviewRemarks.trim();
    if (decision === 'rejected' && !remarks) {
      toast.error('Review remarks are required when rejecting a request.');
      return;
    }
    if (!window.confirm(`Are you sure you want to ${decision === 'approved' ? 'approve' : 'reject'} this payment request?`)) return;

    setIsHrReviewing(true);
    try {
      const reviewedAt = new Date().toISOString();
      const requiresAdminApproval = request?.workflow?.requiresAdminApproval ?? Number(request?.requestedAmount || 0) > 20000;
      const nextStatus = decision === 'rejected'
        ? 'ReturnedByHR'
        : getStatusAfterHrReview(request?.workflow, request?.requestedAmount);
      const hrReview = {
        decision,
        remarks,
        reviewedBy: user?.displayName || user?.email || '',
        reviewedByEmail: user?.email || '',
        reviewedAt
      };

      await updateDoc(doc(db, 'payment_requests', requestId), {
        status: nextStatus,
        hrReview,
        updatedAt: serverTimestamp(),
        auditTrail: arrayUnion({
          action: decision === 'approved' ? 'approved_by_hr' : 'rejected_by_hr',
          at: reviewedAt,
          byUid: user?.uid || '',
          byName: user?.displayName || user?.email || '',
          remarks
        })
      });
      setRequest((current) => ({ ...current, status: nextStatus, hrReview }));
      setHrReviewRemarks('');
      toast.success(decision === 'approved'
        ? (requiresAdminApproval ? 'Payment request sent to Admin for approval.' : 'Payment request sent to Accounts.')
        : 'Payment request returned by HR.');
    } catch (reviewError) {
      console.error('Unable to review payment request:', reviewError);
      toast.error('Unable to save the HR review. Please try again.');
    } finally {
      setIsHrReviewing(false);
    }
  };

  const handleAdminDecision = async (decision) => {
    const remarks = adminReviewRemarks.trim();
    if (decision === 'rejected' && !remarks) {
      toast.error('Review remarks are required when rejecting a request.');
      return;
    }
    if (!window.confirm(`Are you sure you want to ${decision === 'approved' ? 'approve' : 'reject'} this payment request?`)) return;

    setIsAdminReviewing(true);
    try {
      const reviewedAt = new Date().toISOString();
      const nextStatus = decision === 'approved' ? 'Waiting Accounts Payment' : 'ReturnedByAdmin';
      const adminReview = {
        decision,
        remarks,
        reviewedBy: user?.displayName || user?.email || '',
        reviewedByEmail: user?.email || '',
        reviewedAt
      };

      await updateDoc(doc(db, 'payment_requests', requestId), {
        status: nextStatus,
        adminReview,
        updatedAt: serverTimestamp(),
        auditTrail: arrayUnion({
          action: decision === 'approved' ? 'approved_by_admin' : 'returned_by_admin',
          at: reviewedAt,
          byUid: user?.uid || '',
          byName: user?.displayName || user?.email || '',
          remarks
        })
      });
      setRequest((current) => ({ ...current, status: nextStatus, adminReview }));
      setAdminReviewRemarks('');
      toast.success(decision === 'approved'
        ? 'Payment request sent to Accounts for payment processing.'
        : 'Payment request returned to the requester.');
    } catch (reviewError) {
      console.error('Unable to complete Admin approval:', reviewError);
      toast.error('Unable to save the Admin review. Please try again.');
    } finally {
      setIsAdminReviewing(false);
    }
  };

  const openPaymentModal = () => {
    setPaymentForm(emptyPaymentForm());
    setShowPaymentModal(true);
  };

  const handlePaymentFormChange = (event) => {
    const { name, value } = event.target;
    setPaymentForm((current) => ({ ...current, [name]: value }));
  };

  const recordPayment = async (event) => {
    event.preventDefault();
    const settledAmount = Number(paymentForm.settledAmount || 0);
    const tdsAmount = Number(paymentForm.tdsAmount || 0);
    const otherDeductions = Number(paymentForm.otherDeductions || 0);
    const netPayment = settledAmount - tdsAmount - otherDeductions;
    const processedByName = paymentForm.processedByName;

    if (!settledAmount || settledAmount <= 0) return toast.error('Enter the approved amount (gross).');
    if (settledAmount > remainingBalance) return toast.error('The settled amount cannot exceed the remaining balance.');
    if (tdsAmount < 0 || otherDeductions < 0 || netPayment < 0) return toast.error('Deductions cannot exceed the settled amount.');
    if (!paymentForm.paymentDate || !paymentForm.paymentMode || !processedByName) return toast.error('Complete the payment date, mode, and payment processor.');

    setIsRecordingPayment(true);
    try {
      const recordedAt = new Date().toISOString();
      const payment = {
        paymentId: `PRP-${Date.now()}`,
        settledAmount,
        tdsAmount,
        otherDeductions,
        netPayment,
        paymentDate: paymentForm.paymentDate,
        paymentMode: paymentForm.paymentMode,
        paymentReference: paymentForm.paymentReference.trim(),
        remarks: paymentForm.remarks.trim(),
        processedByName,
        recordedByUid: user?.uid || '',
        recordedByName: user?.displayName || user?.email || '',
        recordedAt
      };
      const updatedTotalSettled = totalSettledAmount + settledAmount;
      const updatedTotalTds = totalTdsAmount + tdsAmount;
      const updatedTotalOtherDeductions = totalOtherDeductions + otherDeductions;
      const updatedTotalNetPaid = totalNetPaid + netPayment;
      const updatedBalance = Math.max(0, Number(request?.requestedAmount || 0) - updatedTotalSettled);
      const isFullySettled = updatedBalance === 0;
      const nextStatus = isFullySettled
        ? (request?.requestType === 'Employee Advance' ? 'Advance Paid Awaiting Settlement' : 'Closed')
        : 'Partially Paid';

      await updateDoc(doc(db, 'payment_requests', requestId), {
        payments: arrayUnion(payment),
        totalSettledAmount: updatedTotalSettled,
        totalTdsAmount: updatedTotalTds,
        totalOtherDeductions: updatedTotalOtherDeductions,
        totalNetPaid: updatedTotalNetPaid,
        balanceAmount: updatedBalance,
        status: nextStatus,
        updatedAt: serverTimestamp(),
        auditTrail: arrayUnion({
          action: 'payment_recorded',
          at: recordedAt,
          byUid: user?.uid || '',
          byName: user?.displayName || user?.email || '',
          settledAmount,
          netPayment,
          paymentMode: paymentForm.paymentMode,
          processedByName: payment.processedByName
        })
      });
      setRequest((current) => ({
        ...current,
        payments: [...(current.payments || []), payment],
        totalSettledAmount: updatedTotalSettled,
        totalTdsAmount: updatedTotalTds,
        totalOtherDeductions: updatedTotalOtherDeductions,
        totalNetPaid: updatedTotalNetPaid,
        balanceAmount: updatedBalance,
        status: nextStatus
      }));
      setShowPaymentModal(false);
      toast.success(isFullySettled
        ? (request?.requestType === 'Employee Advance' ? 'Advance payment recorded. Settlement is pending.' : 'Payment recorded and request closed.')
        : 'Payment recorded. The request remains partially paid.');
    } catch (paymentError) {
      console.error('Unable to record payment:', paymentError);
      toast.error('Unable to record the payment. Please try again.');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  return (
    <div className="payment-request-preview" style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <section className="payment-request-preview__actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', padding: '16px 18px', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
          <button type="button" onClick={() => navigate('/payments-requests')} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', color: '#334155', background: '#fff', cursor: 'pointer', fontWeight: '700' }}><ArrowLeft size={16} /> Back</button>
          <h2 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Payment Request: <span style={{ fontWeight: '600' }}>{request?.requestNumber || request?.voucherNo || requestId}</span></h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canManageOwnRequest && <>
            <button type="button" onClick={() => navigate(`/payment-requests/${requestId}/edit`)} style={{ padding: '9px 14px', border: '1px solid #cbd5e1', borderRadius: '7px', background: '#fff', color: '#334155', fontWeight: '700', cursor: 'pointer' }}>Edit</button>
            <button type="button" onClick={submitRequest} disabled={isSubmitting} style={{ padding: '9px 14px', border: 0, borderRadius: '7px', background: isSubmitting ? '#93c5fd' : '#2563eb', color: '#fff', fontWeight: '700', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>{isSubmitting ? 'Submitting…' : 'Submit'}</button>
          </>}
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setShowMoreActions((visible) => !visible)} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', color: '#334155', background: '#fff', fontWeight: '700', cursor: 'pointer' }}><MoreHorizontal size={17} /> More <ChevronDown size={15} /></button>
            {showMoreActions && (
              <div style={{ position: 'absolute', zIndex: 20, top: 'calc(100% + 6px)', right: 0, minWidth: '155px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.16)' }}>
                <button type="button" disabled style={{ display: 'block', width: '100%', padding: '9px 10px', border: 0, borderRadius: '5px', color: '#fca5a5', background: 'transparent', textAlign: 'left', fontWeight: '700', cursor: 'not-allowed' }}>Delete</button>
                <button type="button" onClick={() => { setShowMoreActions(false); window.print(); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', width: '100%', padding: '9px 10px', border: 0, borderRadius: '5px', color: '#1e293b', background: 'transparent', textAlign: 'left', fontWeight: '700', cursor: 'pointer' }}><Printer size={16} /> Print PDF</button>
              </div>
            )}
          </div>
        </div>
      </section>

      {error ? (
        <div style={{ padding: '24px', border: '1px solid #fecaca', borderRadius: '10px', background: '#fef2f2', color: '#b91c1c' }}>{error}</div>
      ) : (
        <>
          {showManagerReviewOutcome && (
            <section style={{ marginBottom: '20px', padding: '16px 18px', border: `1px solid ${request.managerReview.decision === 'rejected' ? '#fecaca' : '#bbf7d0'}`, borderRadius: '12px', background: request.managerReview.decision === 'rejected' ? '#fef2f2' : '#f0fdf4', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              {request.managerReview.decision === 'rejected'
                ? <AlertCircle size={22} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                : <CircleCheck size={22} color="#16a34a" style={{ flexShrink: 0, marginTop: '1px' }} />}
              <div>
                <h3 style={{ margin: '0 0 5px', color: request.managerReview.decision === 'rejected' ? '#991b1b' : '#166534', fontSize: '15px' }}>{request.managerReview.decision === 'rejected' ? 'Returned by Manager' : 'Approved by Manager'}</h3>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: 1.5 }}>{request.managerReview.remarks || (request.managerReview.decision === 'rejected' ? 'No remarks provided.' : 'Your request has been sent to Accounts for checking.')}</p>
                <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '12px' }}>Reviewed by {request.managerReview.reviewedBy || 'Manager'}{request.managerReview.reviewedAt ? ` on ${formatDate(request.managerReview.reviewedAt.slice(0, 10))}` : ''}</p>
              </div>
            </section>
          )}
          {showAccountsCheckOutcome && (
            <section style={{ marginBottom: '20px', padding: '16px 18px', border: `1px solid ${request.accountsCheckReview.decision === 'rejected' ? '#fecaca' : '#bbf7d0'}`, borderRadius: '12px', background: request.accountsCheckReview.decision === 'rejected' ? '#fef2f2' : '#f0fdf4', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              {request.accountsCheckReview.decision === 'rejected'
                ? <AlertCircle size={22} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                : <CircleCheck size={22} color="#16a34a" style={{ flexShrink: 0, marginTop: '1px' }} />}
              <div>
                <h3 style={{ margin: '0 0 5px', color: request.accountsCheckReview.decision === 'rejected' ? '#991b1b' : '#166534', fontSize: '15px' }}>{request.accountsCheckReview.decision === 'rejected' ? 'Returned by Accounts' : 'Checked by Accounts'}</h3>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: 1.5 }}>{request.accountsCheckReview.remarks || (request.accountsCheckReview.decision === 'rejected' ? 'No remarks provided.' : request.status === 'Waiting Admin Approval' ? 'Your request has been sent to Admin for approval.' : request.status === 'Waiting Accounts Payment' ? 'Your request has been sent to Accounts for payment processing.' : 'Your request has been sent to HR for review.')}</p>
                <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '12px' }}>Checked by {request.accountsCheckReview.reviewedBy || 'Accounts'}{request.accountsCheckReview.reviewedAt ? ` on ${formatDate(request.accountsCheckReview.reviewedAt.slice(0, 10))}` : ''}</p>
              </div>
            </section>
          )}
          {showHrReviewOutcome && (
            <section style={{ marginBottom: '20px', padding: '16px 18px', border: `1px solid ${request.hrReview.decision === 'rejected' ? '#fecaca' : '#bbf7d0'}`, borderRadius: '12px', background: request.hrReview.decision === 'rejected' ? '#fef2f2' : '#f0fdf4', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              {request.hrReview.decision === 'rejected'
                ? <AlertCircle size={22} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                : <CircleCheck size={22} color="#16a34a" style={{ flexShrink: 0, marginTop: '1px' }} />}
              <div>
                <h3 style={{ margin: '0 0 5px', color: request.hrReview.decision === 'rejected' ? '#991b1b' : '#166534', fontSize: '15px' }}>{request.hrReview.decision === 'rejected' ? 'Returned by HR' : 'Approved by HR'}</h3>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: 1.5 }}>{request.hrReview.remarks || (request.hrReview.decision === 'rejected' ? 'No remarks provided.' : request.status === 'Waiting Admin Approval' ? 'Your request has been sent to Admin for approval.' : 'Your request has been sent to Accounts.')}</p>
                <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '12px' }}>Reviewed by {request.hrReview.reviewedBy || 'HR'}{request.hrReview.reviewedAt ? ` on ${formatDate(request.hrReview.reviewedAt.slice(0, 10))}` : ''}</p>
              </div>
            </section>
          )}
          {showAdminReviewOutcome && (
            <section style={{ marginBottom: '20px', padding: '16px 18px', border: `1px solid ${request.adminReview.decision === 'rejected' ? '#fecaca' : '#bbf7d0'}`, borderRadius: '12px', background: request.adminReview.decision === 'rejected' ? '#fef2f2' : '#f0fdf4', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              {request.adminReview.decision === 'rejected'
                ? <AlertCircle size={22} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                : <CircleCheck size={22} color="#16a34a" style={{ flexShrink: 0, marginTop: '1px' }} />}
              <div>
                <h3 style={{ margin: '0 0 5px', color: request.adminReview.decision === 'rejected' ? '#991b1b' : '#166534', fontSize: '15px' }}>{request.adminReview.decision === 'rejected' ? 'Returned by Admin' : 'Approved by Admin'}</h3>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: 1.5 }}>{request.adminReview.remarks || (request.adminReview.decision === 'rejected' ? 'No remarks provided.' : 'Your request has been sent to Accounts for payment processing.')}</p>
                <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '12px' }}>Reviewed by {request.adminReview.reviewedBy || 'Admin'}{request.adminReview.reviewedAt ? ` on ${formatDate(request.adminReview.reviewedAt.slice(0, 10))}` : ''}</p>
              </div>
            </section>
          )}
          {canCreateOrContinueSettlement && (
            <section style={{ marginBottom: '20px', padding: '18px', border: '1px solid #bfdbfe', borderRadius: '12px', background: '#f8fbff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '18px' }}>
              <div>
                <h3 style={{ margin: '0 0 6px', color: '#1e3a8a', fontSize: '16px' }}>Advance Settlement</h3>
                <p style={{ margin: '0 0 4px', color: '#475569', fontSize: '14px' }}><strong>Advance Received:</strong> {formatAmount(totalNetPaid || request?.totalNetPaid || request?.totalSettledAmount || 0)}</p>
                <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>Status: {request?.settlementStatus || 'Not started'}</p>
              </div>
              {['Advance Paid Awaiting Settlement', 'Settlement Returned for Revision'].includes(request?.status) && (
                <button type="button" onClick={() => navigate(request?.activeSettlementId ? `/payment-requests/${requestId}/settlements/${request.activeSettlementId}/edit` : `/payment-requests/${requestId}/settlement/new`)} style={{ padding: '10px 14px', border: 0, borderRadius: '7px', background: '#2563eb', color: '#fff', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>{request?.activeSettlementId ? 'Continue Settlement' : 'Create Settlement'}</button>
              )}
              {request?.status === 'Settlement Under Review' && request?.activeSettlementId && (
                <button type="button" onClick={() => navigate(`/payment-requests/${requestId}/settlements/${request.activeSettlementId}`)} style={{ padding: '10px 14px', border: '1px solid #bfdbfe', borderRadius: '7px', background: '#eff6ff', color: '#1d4ed8', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>View Settlement</button>
              )}
            </section>
          )}
          {canManagerReview && (
            <section style={{ marginBottom: '20px', padding: '20px', border: '1px solid #bfdbfe', borderRadius: '12px', background: '#eff6ff' }}>
              <h3 style={{ margin: '0 0 6px', color: '#1e3a8a', fontSize: '16px' }}>Manager Review</h3>
              <p style={{ margin: '0 0 14px', color: '#475569', fontSize: '13px' }}>Review the request and add remarks before making your decision. Remarks are required when rejecting.</p>
              <textarea
                value={managerReviewRemarks}
                onChange={(event) => setManagerReviewRemarks(event.target.value)}
                rows="3"
                placeholder="Add review remarks..."
                disabled={isReviewing}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #93c5fd', borderRadius: '7px', background: '#fff', color: '#0f172a', fontSize: '14px', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                <button type="button" onClick={() => handleManagerDecision('rejected')} disabled={isReviewing} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: '1px solid #dc2626', borderRadius: '7px', background: '#fff', color: '#dc2626', fontWeight: '700', cursor: isReviewing ? 'not-allowed' : 'pointer' }}><X size={16} /> Reject</button>
                <button type="button" onClick={() => handleManagerDecision('approved')} disabled={isReviewing} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: 0, borderRadius: '7px', background: '#16a34a', color: '#fff', fontWeight: '700', cursor: isReviewing ? 'not-allowed' : 'pointer' }}><Check size={16} /> {isReviewing ? 'Saving…' : 'Approve'}</button>
              </div>
            </section>
          )}
          {canAccountsCheck && (
            <section style={{ marginBottom: '20px', padding: '20px', border: '1px solid #bfdbfe', borderRadius: '12px', background: '#eff6ff' }}>
              <h3 style={{ margin: '0 0 6px', color: '#1e3a8a', fontSize: '16px' }}>Accounts Check</h3>
              <p style={{ margin: '0 0 14px', color: '#475569', fontSize: '13px' }}>Check the request and add remarks before making your decision. Remarks are required when returning it.</p>
              <textarea
                value={accountsCheckRemarks}
                onChange={(event) => setAccountsCheckRemarks(event.target.value)}
                rows="3"
                placeholder="Add check remarks..."
                disabled={isAccountsChecking}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #93c5fd', borderRadius: '7px', background: '#fff', color: '#0f172a', fontSize: '14px', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                <button type="button" onClick={() => handleAccountsCheckDecision('rejected')} disabled={isAccountsChecking} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: '1px solid #dc2626', borderRadius: '7px', background: '#fff', color: '#dc2626', fontWeight: '700', cursor: isAccountsChecking ? 'not-allowed' : 'pointer' }}><X size={16} /> Return</button>
                <button type="button" onClick={() => handleAccountsCheckDecision('approved')} disabled={isAccountsChecking} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: 0, borderRadius: '7px', background: '#16a34a', color: '#fff', fontWeight: '700', cursor: isAccountsChecking ? 'not-allowed' : 'pointer' }}><Check size={16} /> {isAccountsChecking ? 'Saving…' : 'Approve'}</button>
              </div>
            </section>
          )}
          {canHrReview && (
            <section style={{ marginBottom: '20px', padding: '20px', border: '1px solid #bfdbfe', borderRadius: '12px', background: '#eff6ff' }}>
              <h3 style={{ margin: '0 0 6px', color: '#1e3a8a', fontSize: '16px' }}>HR Review</h3>
              <p style={{ margin: '0 0 14px', color: '#475569', fontSize: '13px' }}>Review the request and add remarks before making your decision. Remarks are required when rejecting.</p>
              <textarea
                value={hrReviewRemarks}
                onChange={(event) => setHrReviewRemarks(event.target.value)}
                rows="3"
                placeholder="Add review remarks..."
                disabled={isHrReviewing}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #93c5fd', borderRadius: '7px', background: '#fff', color: '#0f172a', fontSize: '14px', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                <button type="button" onClick={() => handleHrDecision('rejected')} disabled={isHrReviewing} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: '1px solid #dc2626', borderRadius: '7px', background: '#fff', color: '#dc2626', fontWeight: '700', cursor: isHrReviewing ? 'not-allowed' : 'pointer' }}><X size={16} /> Reject</button>
                <button type="button" onClick={() => handleHrDecision('approved')} disabled={isHrReviewing} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: 0, borderRadius: '7px', background: '#16a34a', color: '#fff', fontWeight: '700', cursor: isHrReviewing ? 'not-allowed' : 'pointer' }}><Check size={16} /> {isHrReviewing ? 'Saving…' : 'Approve'}</button>
              </div>
            </section>
          )}
          {canAdminReview && (
            <section style={{ marginBottom: '20px', padding: '20px', border: '1px solid #bfdbfe', borderRadius: '12px', background: '#eff6ff' }}>
              <h3 style={{ margin: '0 0 6px', color: '#1e3a8a', fontSize: '16px' }}>Admin Approval</h3>
              <p style={{ margin: '0 0 14px', color: '#475569', fontSize: '13px' }}>Review the request and add remarks before making your decision. Remarks are required when rejecting.</p>
              <textarea
                value={adminReviewRemarks}
                onChange={(event) => setAdminReviewRemarks(event.target.value)}
                rows="3"
                placeholder="Add approval remarks..."
                disabled={isAdminReviewing}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #93c5fd', borderRadius: '7px', background: '#fff', color: '#0f172a', fontSize: '14px', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                <button type="button" onClick={() => handleAdminDecision('rejected')} disabled={isAdminReviewing} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: '1px solid #dc2626', borderRadius: '7px', background: '#fff', color: '#dc2626', fontWeight: '700', cursor: isAdminReviewing ? 'not-allowed' : 'pointer' }}><X size={16} /> Reject</button>
                <button type="button" onClick={() => handleAdminDecision('approved')} disabled={isAdminReviewing} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: 0, borderRadius: '7px', background: '#16a34a', color: '#fff', fontWeight: '700', cursor: isAdminReviewing ? 'not-allowed' : 'pointer' }}><Check size={16} /> {isAdminReviewing ? 'Savingâ€¦' : 'Approve'}</button>
              </div>
            </section>
          )}
          {canProcessPayment && (
            <section style={{ marginBottom: '20px', padding: '20px', border: '1px solid #bbf7d0', borderRadius: '12px', background: '#f0fdf4' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: '0 0 6px', color: '#166534', fontSize: '16px' }}>Payment Processing</h3>
                  <p style={{ margin: 0, color: '#475569', fontSize: '13px' }}>Record a completed payment. The request status and balance will update automatically.</p>
                </div>
                <button type="button" onClick={openPaymentModal} style={{ padding: '9px 14px', border: 0, borderRadius: '7px', background: '#16a34a', color: '#fff', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>Record Payment</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: '12px' }}>
                {[
                  ['Amount Requested', formatAmount(request?.requestedAmount)],
                  ['Settled So Far', formatAmount(totalSettledAmount)],
                  ['TDS / Other', formatAmount(totalTdsAmount + totalOtherDeductions)],
                  ['Net Paid', formatAmount(totalNetPaid)],
                  ['Balance Remaining', formatAmount(remainingBalance)]
                ].map(([label, value]) => (
                  <div key={label} style={{ padding: '12px', border: '1px solid #d1fae5', borderRadius: '8px', background: '#fff' }}>
                    <div style={{ marginBottom: '4px', color: '#64748b', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>{label}</div>
                    <strong style={{ color: '#0f172a', fontSize: '15px' }}>{value}</strong>
                  </div>
                ))}
              </div>
            </section>
          )}
          <section className="payment-request-preview__document">
            <div className="payment-request-document">
              <table className="payment-request-document__table payment-request-document__table--header">
                <colgroup><col style={{ width: '18%' }} /><col style={{ width: '52%' }} /><col style={{ width: '12%' }} /><col style={{ width: '18%' }} /></colgroup>
                <tbody>
                  <tr><th className="payment-request-document__section-title" colSpan="4">PAYMENT / ADVANCE REQUISITION &amp; APPROVAL FORM</th></tr>
                  <tr><td className="payment-request-document__field-label">Legal Entity</td><td>{request.legalEntityName || request.legalEntity || request.costCentre || '—'}</td><td className="payment-request-document__field-label">Request Date</td><td>{formatDate(request.requestDate)}</td></tr>
                  <tr><td className="payment-request-document__field-label">Payee Name</td><td>{request.payeeName || '—'}</td><td className="payment-request-document__field-label">Payee Type</td><td>{request.payeeType || '—'}</td></tr>
                  <tr><td className="payment-request-document__field-label">ORDER / PO / RO</td><td>{request.orderPoRo || '—'}</td><td className="payment-request-document__field-label">Pay Until</td><td>{formatDate(request.payUntil)}</td></tr>
                  <tr><td className="payment-request-document__field-label">Purpose / Business Justification</td><td className="payment-request-document__remarks" colSpan="3">{request.paymentAdvanceRemarks || '—'}</td></tr>
                </tbody>
              </table>

              <table className="payment-request-document__table payment-request-document__table--amount">
                <colgroup><col style={{ width: '15%' }} /><col style={{ width: '15%' }} /><col style={{ width: '55%' }} /><col style={{ width: '15%' }} /></colgroup>
                <tbody>
                  <tr><th className="payment-request-document__section-title" colSpan="4">AMOUNT BREAKUP &amp; JUSTIFICATION</th></tr>
                  <tr><th>Against / Head</th><th>Document No.</th><th>Description</th><th>Amount</th></tr>
                  {displayLineItems.map((item, index) => <tr className="payment-request-document__line-row" key={`${item.againstHead || 'blank'}-${index}`}><td>{item.againstHead || ''}</td><td>{item.documentNo || ''}</td><td>{item.description || ''}</td><td>{item.amount ? formatAmount(item.amount) : ''}</td></tr>)}
                  <tr><td rowSpan="3" className="payment-request-document__field-label">Outstanding Previous Advance<br /><br />{request.outstandingPreviousAdvance || '—'}</td><td rowSpan="3" /><td className="payment-request-document__summary-label">Amount Requested</td><td className="payment-request-document__amount-value">{formatAmount(request.requestedAmount)}</td></tr>
                  <tr><td className="payment-request-document__summary-label">TDS / Others</td><td className="payment-request-document__amount-value">{payments.length > 0 ? formatAmount(totalTdsAmount + totalOtherDeductions) : '—'}</td></tr>
                  <tr><td className="payment-request-document__summary-label">Net Pay</td><td className="payment-request-document__amount-value">{payments.length > 0 ? formatAmount(totalNetPaid) : '—'}</td></tr>
                </tbody>
              </table>

              <table className="payment-request-document__table payment-request-document__table--finance">
                <tbody>
                  <tr><th className="payment-request-document__section-title" colSpan="4">FOR FINANCE USE ONLY</th></tr>
                  <tr><td className="payment-request-document__field-label">MODE OF PAYMENT</td><td colSpan="3"><div style={{ display: 'flex', justifyContent: 'space-around', gap: '10px' }}>{['CASH', 'NEFT', 'CHEQUE', 'UPI', 'OTHER'].map((method) => <span key={method} style={{ minWidth: '46px' }}>{method}<span className="payment-request-document__checkbox">{String(latestPayment?.paymentMode || '').toUpperCase() === method ? '✓' : ''}</span></span>)}</div></td></tr>
                  <tr><td className="payment-request-document__field-label">PAYMENT REF. / CHQ NO.</td><td colSpan="3">{latestPayment?.paymentReference || '—'}</td></tr>
                  <tr><td className="payment-request-document__finance-label" style={{ textAlign: 'left' }}>APPROVED AMT. / DT.</td><td>{payments.length > 0 ? `${formatAmount(totalSettledAmount)} / ${formatDate(latestPayment?.paymentDate)}` : '—'}</td><td className="payment-request-document__finance-label">BALANCE AMT. / DT.</td><td>{payments.length > 0 ? `${formatAmount(remainingBalance)} / ${formatDate(latestPayment?.paymentDate)}` : '—'}</td></tr>
                  <tr><td className="payment-request-document__field-label">PAYMENT PROCESSED BY</td><td>{latestPayment?.processedByName || '—'}</td><td className="payment-request-document__field-label" style={{ textAlign: 'center' }}>POSTED BY</td><td>—</td></tr>
                </tbody>
              </table>

              <table className="payment-request-document__table payment-request-document__table--approval">
                <colgroup><col style={{ width: '18%' }} /><col style={{ width: '38%' }} /><col style={{ width: '24%' }} /><col style={{ width: '20%' }} /></colgroup>
                <tbody>
                  <tr><th className="payment-request-document__section-title" colSpan="4">APPROVAL WORKFLOW</th></tr>
                  <tr><th className="payment-request-document__workflow-label">STAGE</th><th>NAME</th><th>DATE</th><th>SIGNATURE</th></tr>
                  <tr className="payment-request-document__workflow-row"><td className="payment-request-document__workflow-label">REQUEST BY</td><td>{request.requesterName || '—'}</td><td>{formatDate(request.requestDate)}</td><td /></tr>
                  {approvalWorkflow.map(({ stage, review }) => <tr className="payment-request-document__workflow-row" key={stage}><td className="payment-request-document__workflow-label">{stage}</td><td>{review?.reviewedBy || ''}</td><td>{formatDateTime(review?.reviewedAt)}</td><td /></tr>)}
                </tbody>
              </table>
            </div>
          </section>

          {canViewPaymentHistory && (
            <section style={{ marginTop: '20px', padding: '20px', border: '1px solid #d1fae5', borderRadius: '12px', background: '#fff' }}>
              <h3 style={{ margin: '0 0 6px', color: '#166534', fontSize: '16px' }}>Payment History</h3>
              <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '13px' }}>Settled: <strong>{formatAmount(totalSettledAmount)}</strong> · Deductions: <strong>{formatAmount(totalTdsAmount + totalOtherDeductions)}</strong> · Net paid: <strong>{formatAmount(totalNetPaid)}</strong> · Balance: <strong>{formatAmount(remainingBalance)}</strong></p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: '720px', borderCollapse: 'collapse', border: '1px solid #d1fae5' }}>
                  <thead><tr style={{ background: '#ecfdf5' }}>{['Date', 'Mode', 'Reference', 'Settled', 'TDS / Other', 'Net Paid', 'Processed By'].map((heading) => <th key={heading} style={{ padding: '9px 10px', borderBottom: '1px solid #d1fae5', color: '#475569', fontSize: '11px', textTransform: 'uppercase', textAlign: 'left' }}>{heading}</th>)}</tr></thead>
                  <tbody>{payments.map((payment) => <tr key={payment.paymentId} style={{ borderBottom: '1px solid #ecfdf5' }}><td style={{ padding: '10px' }}>{formatDate(payment.paymentDate)}</td><td style={{ padding: '10px' }}>{payment.paymentMode || '—'}</td><td style={{ padding: '10px' }}>{payment.paymentReference || '—'}</td><td style={{ padding: '10px' }}>{formatAmount(payment.settledAmount)}</td><td style={{ padding: '10px' }}>{formatAmount(Number(payment.tdsAmount || 0) + Number(payment.otherDeductions || 0))}</td><td style={{ padding: '10px', fontWeight: '700' }}>{formatAmount(payment.netPayment)}</td><td style={{ padding: '10px' }}>{payment.processedByName || '—'}</td></tr>)}</tbody>
                </table>
              </div>
            </section>
          )}

          <section className="payment-request-preview__details" style={{ padding: '24px', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#fff' }}>
            <h3 style={{ margin: '0 0 20px', color: '#0f172a' }}>Request Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
              <div><div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>REQUEST TYPE</div><strong>{request.requestType || '—'}</strong></div>
              <div><div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>LEGAL ENTITY</div><strong>{request.legalEntityName || request.legalEntity || request.costCentre || '—'}</strong></div>
              <div><div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>PAYEE TYPE</div><strong>{request.payeeType || '—'}</strong></div>
              <div><div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>PAYEE NAME</div><strong>{request.payeeName || '—'}</strong></div>
              <div><div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>REQUEST DATE</div><strong>{request.requestDate || '—'}</strong></div>
              <div><div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>PAY UNTIL</div><strong>{request.payUntil || '—'}</strong></div>
              <div><div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>ORDER / PO / RO</div><strong>{request.orderPoRo || '—'}</strong></div>
              <div><div style={{ color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>AMOUNT REQUESTED</div><strong>{formatAmount(request.requestedAmount)}</strong></div>
            </div>
            {request.attachments?.length > 0 && (
              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px', color: '#334155', fontSize: '14px' }}>Attached Documents</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
                  {request.attachments.map((attachment, index) => (
                    <AttachmentCard
                      key={`${attachment.url || attachment.name}-${index}`}
                      href={attachment.url}
                      label={attachment.name || `Attachment ${index + 1}`}
                      name={attachment.name}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>
          {showPaymentModal && (
            <div role="presentation" onMouseDown={() => !isRecordingPayment && setShowPaymentModal(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: 'rgba(15, 23, 42, 0.5)' }}>
              <form onSubmit={recordPayment} onMouseDown={(event) => event.stopPropagation()} style={{ width: 'min(620px, 100%)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', padding: '24px', borderRadius: '12px', background: '#fff', boxShadow: '0 20px 45px rgba(15, 23, 42, 0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '20px' }}>
                  <div><h3 style={{ margin: '0 0 5px', color: '#0f172a' }}>Record Payment</h3><p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>Remaining balance: <strong>{formatAmount(remainingBalance)}</strong></p></div>
                  <button type="button" onClick={() => setShowPaymentModal(false)} disabled={isRecordingPayment} aria-label="Close payment modal" style={{ width: '32px', height: '32px', border: 0, borderRadius: '6px', background: '#f1f5f9', color: '#475569', cursor: 'pointer' }}><X size={18} /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                  <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>Approved Amount (Gross)<input required name="settledAmount" type="number" min="0" max={remainingBalance} step="0.01" value={paymentForm.settledAmount} onChange={handlePaymentFormChange} style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '14px' }} /></label>
                  <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>Payment date<input required name="paymentDate" type="date" value={paymentForm.paymentDate} onChange={handlePaymentFormChange} style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '14px' }} /></label>
                  <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>TDS<input name="tdsAmount" type="number" min="0" step="0.01" value={paymentForm.tdsAmount} onChange={handlePaymentFormChange} placeholder="0" style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '14px' }} /></label>
                  <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>Other deductions<input name="otherDeductions" type="number" min="0" step="0.01" value={paymentForm.otherDeductions} onChange={handlePaymentFormChange} placeholder="0" style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '14px' }} /></label>
                  <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>Mode of payment<select required name="paymentMode" value={paymentForm.paymentMode} onChange={handlePaymentFormChange} style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', background: '#fff', fontSize: '14px' }}><option value="">Select mode</option>{['Cash', 'NEFT', 'Cheque', 'UPI', 'Other'].map((mode) => <option key={mode} value={mode}>{mode}</option>)}</select></label>
                  <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' }}>Payment processed by<select required name="processedByName" value={paymentForm.processedByName} onChange={handlePaymentFormChange} style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', background: '#fff', fontSize: '14px' }}><option value="">Select payment processor</option>{PAYMENT_PROCESSORS.map((processor) => <option key={processor} value={processor}>{processor}</option>)}</select></label>
                  <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700', gridColumn: '1 / -1' }}>Payment reference / cheque no.<input name="paymentReference" value={paymentForm.paymentReference} onChange={handlePaymentFormChange} placeholder="UTR, cheque no., or cash reference" style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '14px' }} /></label>
                  <label style={{ display: 'grid', gap: '6px', color: '#334155', fontSize: '13px', fontWeight: '700', gridColumn: '1 / -1' }}>Remarks<textarea name="remarks" value={paymentForm.remarks} onChange={handlePaymentFormChange} rows="3" placeholder="Optional payment remarks" style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '14px', resize: 'vertical' }} /></label>
                </div>
                <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', background: '#f8fafc', color: '#334155', fontSize: '14px' }}>Net Pay: <strong>{formatAmount(Math.max(0, Number(paymentForm.settledAmount || 0) - Number(paymentForm.tdsAmount || 0) - Number(paymentForm.otherDeductions || 0)))}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}><button type="button" onClick={() => setShowPaymentModal(false)} disabled={isRecordingPayment} style={{ padding: '9px 14px', border: '1px solid #cbd5e1', borderRadius: '7px', background: '#fff', color: '#334155', fontWeight: '700', cursor: 'pointer' }}>Cancel</button><button type="submit" disabled={isRecordingPayment} style={{ padding: '9px 14px', border: 0, borderRadius: '7px', background: isRecordingPayment ? '#86efac' : '#16a34a', color: '#fff', fontWeight: '700', cursor: isRecordingPayment ? 'not-allowed' : 'pointer' }}>{isRecordingPayment ? 'Recording…' : 'Record Payment'}</button></div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentRequestPreview;
